import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useBlockedUsersList } from "@/hooks/useBlockedUsersList";
import { userQueryKeys } from "@/hooks/userQueryKeys";
import type { User } from "@/lib/humhub-api/types";
import { usersApi } from "@/lib/humhub-api/users";

interface BlockedUserDetail {
  id: string;
  displayName: string | null;
  imageUrl: string | null;
  isLoading: boolean;
  isError: boolean;
}

export const useBlockedUsersDetails = (enabled = true) => {
  const {
    data: blockedUserIds = [],
    isLoading,
    isError,
    refetch,
  } = useBlockedUsersList({ enabled });

  const userQueries = useQueries({
    queries: blockedUserIds.map((id) => {
      const numericId = Number.parseInt(id, 10);
      return {
        queryKey: userQueryKeys.byId(numericId),
        queryFn: () => usersApi.getUserById(numericId),
        enabled: enabled && Number.isInteger(numericId),
        staleTime: 1000 * 60 * 5,
      };
    }),
  }) as UseQueryResult<User, Error>[];

  const blockedUsers = useMemo<BlockedUserDetail[]>(() => {
    return blockedUserIds.map((id, index) => {
      const query = userQueries[index];
      const user = query?.data;

      return {
        id,
        displayName: user?.display_name ?? null,
        imageUrl: user?.image_url ?? null,
        isLoading: query?.isLoading ?? false,
        isError: query?.isError ?? false,
      };
    });
  }, [blockedUserIds, userQueries]);

  const refresh = async () => {
    await refetch();
    await Promise.all(userQueries.map((query) => query.refetch()));
  };

  return {
    blockedUsers,
    blockedUserIds,
    isLoading,
    isError,
    refresh,
  };
};
